'use client';

import { composeTailwindRenderProps } from '@utils';
import {
	DisclosureGroup as Accordion,
	Button,
	Disclosure as Collapsible,
	DisclosurePanel as CollapsiblePanel,
	composeRenderProps,
	Heading,
} from 'react-aria-components';
import { tv } from 'tailwind-variants';

import type {
	DisclosureGroupProps as AccordionProps,
	ButtonProps,
	DisclosureProps as CollapsibleProps,
	DisclosurePanelProps as DisclosurePanelPrimitiveProps,
} from 'react-aria-components';

import { ChevronLeft } from '../icons';

interface DisclosureGroupProps extends AccordionProps {
	ref?: React.RefObject<HTMLDivElement>;
}
const DisclosureGroup = ({ children, ref, className, ...props }: DisclosureGroupProps) => {
	return (
		<Accordion
			ref={ref}
			data-slot="disclosure-group"
			{...props}
			className={composeTailwindRenderProps(
				className,
				'peer cursor-pointer data-disabled:cursor-not-allowed data-disabled:opacity-75',
			)}
		>
			{values => (
				<div data-slot="disclosure-content">
					{typeof children === 'function' ? children(values) : children}
				</div>
			)}
		</Accordion>
	);
};

const disclosure = tv({
	base: ['peer group/disclosure w-full'],
	variants: {
		isDisabled: {
			true: 'cursor-not-allowed opacity-70',
		},
	},
});

interface DisclosureProps extends CollapsibleProps {
	ref?: React.Ref<HTMLDivElement>;
}
const Disclosure = ({ className, ref, ...props }: DisclosureProps) => {
	return (
		<Collapsible
			ref={ref}
			data-slot="disclosure"
			{...props}
			className={composeRenderProps(className, (className, renderProps) =>
				disclosure({ ...renderProps, className }),
			)}
		>
			{props.children}
		</Collapsible>
	);
};

const disclosureTrigger = tv({
	base: [
		'flex w-full items-center justify-between gap-x-2 py-3 text-left text-sm font-medium',
		'group/trigger **:[span]:flex **:[span]:items-center **:[span]:gap-x-1 [&[aria-expanded=true]_[data-slot=disclosure-chevron]]:-rotate-90',
	],
	variants: {
		isFocused: {
			true: 'outline-hidden',
		},
		isOpen: {
			true: 'text-light-text',
		},
		isDisabled: {
			true: 'cursor-default opacity-50',
		},
	},
});

interface DisclosureTriggerProps extends ButtonProps {
	ref?: React.Ref<HTMLButtonElement>;
}
const DisclosureTrigger = ({ className, ref, ...props }: DisclosureTriggerProps) => {
	return (
		<Heading>
			<Button
				ref={ref}
				slot="trigger"
				className={composeRenderProps(className, (className, renderProps) =>
					disclosureTrigger({
						...renderProps,
						className,
					}),
				)}
				{...props}
			>
				{values => (
					<>
						{typeof props.children === 'function' ? props.children(values) : props.children}
						<ChevronLeft
							data-slot="disclosure-chevron"
							className="size-icon-xs text-light-action-secondary ml-auto shrink-0 transition duration-300"
						/>
					</>
				)}
			</Button>
		</Heading>
	);
};

interface DisclosurePanelProps extends DisclosurePanelPrimitiveProps {
	ref?: React.Ref<HTMLDivElement>;
}
const DisclosurePanel = ({ className, ref, ...props }: DisclosurePanelProps) => {
	return (
		<CollapsiblePanel
			ref={ref}
			data-slot="disclosure-panel"
			className={composeTailwindRenderProps(
				className,
				'text-light-text-lighter overflow-visible text-sm transition-[height,opacity] duration-300 ease-in-out data-[entering]:animate-[disclosure-enter_300ms_ease-in-out] data-[exiting]:animate-[disclosure-exit_300ms_ease-in-out] **:data-[slot=disclosure-group]:border-t **:data-[slot=disclosure-group]:**:[.internal-chevron]:hidden has-data-[slot=disclosure-group]:**:[button]:px-4',
			)}
			{...props}
		>
			<div
				data-slot="disclosure-panel-content"
				className="p-0 [&:has([data-slot=disclosure-group])_&]:px-11"
			>
				{props.children}
			</div>
		</CollapsiblePanel>
	);
};

export type { DisclosureGroupProps, DisclosureProps, DisclosurePanelProps, DisclosureTriggerProps };
export { DisclosureGroup, Disclosure, DisclosurePanel, DisclosureTrigger };
